/**
 * Style Transfer source and reference roles.
 *
 * The toolbar entry is pre-wired to `fal-ai/image-apps-v2/style-transfer`.
 * That endpoint takes `image_url` (the picture to restyle) and an optional
 * `style_reference_image_url`. When the reference is sent, the endpoint infers
 * the style from it instead of `target_style`. It has no strength control.
 *
 * Other tools (inpaint, virtual try-on, face retouching, typography, outpaint)
 * must not go through this helper. It returns null unless the node sub-purpose
 * is `style-transfer`.
 */

export const STYLE_TRANSFER_SUB_PURPOSE = 'style-transfer'
export const STYLE_TRANSFER_SOURCE_PARAM = 'image_url'
export const STYLE_TRANSFER_REFERENCE_PARAM = 'style_reference_image_url'
export const STYLE_TRANSFER_SOURCE_LABEL = 'Source image'
export const STYLE_TRANSFER_REFERENCE_LABEL = 'Style reference'

export const STYLE_TRANSFER_SOURCE_TOOLTIP = 'Source image. The picture to restyle.'
export const STYLE_TRANSFER_REFERENCE_TOOLTIP =
  'Style reference. When connected, its look is used instead of Target style. This model has no strength control.'

export const STYLE_TRANSFER_PRECEDENCE =
  'A connected style reference is used instead of Target style. This model has no strength control.'

export const STYLE_TRANSFER_TEXT_ONLY_LIMIT =
  'This model applies a preset or text style, not a reference image. In Model, choose Style Transfer to use a style reference.'

export const STYLE_TRANSFER_SOURCE_MISSING = 'Connect a source image.'
export const STYLE_TRANSFER_REFERENCE_LOADING = 'The style reference is still loading.'
export const STYLE_TRANSFER_REFERENCE_FAILED = 'The style reference couldn’t be loaded. Reconnect it or choose another image.'
export const STYLE_TRANSFER_UNUSED_LABEL = 'Not used'

export type StyleTransferRoleStatus = 'empty' | 'connected' | 'loading' | 'error'

export interface StyleTransferHandleRef {
  id: string
  type?: string
  dataType?: string
  parameterName?: string
  /** Visual order on the node. Lower is higher on the left edge. */
  ordered?: number
}

export interface StyleTransferConnection {
  handleId: string
  url?: string
  /** A wire is attached but the picture is not readable yet. */
  pending?: boolean
  failed?: boolean
}

export interface StyleTransferRoleView {
  role: 'source' | 'reference' | 'unused'
  param?: string
  label: string
  tooltip: string
  handleId?: string
  url?: string
  status: StyleTransferRoleStatus
  message?: string
}

export interface StyleTransferPresentation {
  supportsReference: boolean
  roles: StyleTransferRoleView[]
  /**
   * Provider fields. `image` is the source only, so a generic image fallback
   * cannot turn the source into the reference. The reference field is omitted
   * when nothing is connected — never filled with the source.
   */
  fields: Record<string, string>
  sourceUrl?: string
  referenceUrl?: string
  blockReason?: string
  precedence?: string
  limitation?: string
}

interface NamedParam {
  name: string
  inputType?: string
  label?: string
  description?: string
}

/** True only for the reference field on the Style Transfer endpoint. Other image params stay on the generic path. */
export function isStyleReferenceParam(name: string): boolean {
  return name === STYLE_TRANSFER_REFERENCE_PARAM
}

export function styleTransferSupportsReference(paramNames: readonly string[]): boolean {
  return paramNames.includes(STYLE_TRANSFER_REFERENCE_PARAM)
}

/**
 * Put the source image parameter before the style reference so the upper
 * handle is the source. No-op unless this parameter list actually has a
 * style reference — other tools keep their order.
 */
export function orderStyleTransferInputParams<T extends NamedParam>(params: readonly T[]): T[] {
  if (!styleTransferSupportsReference(params.map((param) => param.name))) return params as T[]
  const rank = (name: string) => (
    name === STYLE_TRANSFER_SOURCE_PARAM ? 0 : name === STYLE_TRANSFER_REFERENCE_PARAM ? 1 : 2
  )
  const imageIndexes = params
    .map((param, index) => (isImageParam(param) ? index : -1))
    .filter((index) => index >= 0)
  if (imageIndexes.length < 2) return params as T[]
  const ordered = imageIndexes.map((index) => params[index]).sort((a, b) => rank(a.name) - rank(b.name) || 0)
  const next = params.slice()
  imageIndexes.forEach((index, slot) => {
    next[index] = ordered[slot]
  })
  return next
}

/** Role labels for the two Style Transfer image inputs. Other parameters are unchanged. */
export function labelStyleTransferInputParams<T extends NamedParam>(params: readonly T[]): T[] {
  if (!styleTransferSupportsReference(params.map((param) => param.name))) return params as T[]
  return params.map((param) => {
    if (param.name === STYLE_TRANSFER_SOURCE_PARAM) {
      return { ...param, label: STYLE_TRANSFER_SOURCE_LABEL, description: STYLE_TRANSFER_SOURCE_TOOLTIP }
    }
    if (param.name === STYLE_TRANSFER_REFERENCE_PARAM) {
      return { ...param, label: STYLE_TRANSFER_REFERENCE_LABEL, description: STYLE_TRANSFER_REFERENCE_TOOLTIP }
    }
    return param
  })
}

/**
 * Labeled previews and the exact request fields for a Style Transfer node.
 * Returns null for every other sub-purpose.
 */
export function presentStyleTransfer(input: {
  subPurpose?: string
  /** Authoritative when the selected model has loaded. Empty means "not loaded yet". */
  paramNames?: readonly string[]
  handles?: readonly StyleTransferHandleRef[]
  connections?: readonly StyleTransferConnection[]
}): StyleTransferPresentation | null {
  if (input.subPurpose !== STYLE_TRANSFER_SUB_PURPOSE) return null

  const handles = input.handles ?? []
  const connections = input.connections ?? []
  const loadedNames = input.paramNames ?? []
  const namedOnHandles = handles.map((handle) => handle.parameterName).filter((name): name is string => Boolean(name))
  const imageHandles = handles
    .filter((handle) => handle.type !== 'output' && (handle.dataType === 'image' || (!handle.dataType && isImageHandleId(handle.id))))
    .sort((a, b) => handleOrder(a) - handleOrder(b) || a.id.localeCompare(b.id))
  const supportsReference = loadedNames.length > 0
    ? styleTransferSupportsReference(loadedNames)
    : namedOnHandles.includes(STYLE_TRANSFER_REFERENCE_PARAM) || imageHandles.length >= 2
  // The contract lists the reference before the source. Matching in that order
  // put the reference on the upper handle, so a saved graph can have the names
  // swapped. The upper image handle is the source; the next is the reference.
  const sourceHandle = supportsReference
    ? imageHandles[0]
    : imageHandles.find((handle) => handle.parameterName === STYLE_TRANSFER_SOURCE_PARAM)
      ?? imageHandles.find((handle) => handle.id === 'image-input')
      ?? imageHandles[0]
  const referenceHandle = supportsReference ? imageHandles[1] : undefined

  const sourceConnection = connectionFor(connections, sourceHandle?.id)
  const referenceConnection = connectionFor(connections, referenceHandle?.id)
  const sourceUrl = httpUrl(sourceConnection?.url)
  const referenceUrl = httpUrl(referenceConnection?.url)

  const roles: StyleTransferRoleView[] = [
    roleView('source', STYLE_TRANSFER_SOURCE_PARAM, STYLE_TRANSFER_SOURCE_LABEL, STYLE_TRANSFER_SOURCE_TOOLTIP, sourceHandle?.id, sourceConnection, 'Not connected'),
  ]

  if (supportsReference) {
    roles.push(roleView(
      'reference',
      STYLE_TRANSFER_REFERENCE_PARAM,
      STYLE_TRANSFER_REFERENCE_LABEL,
      STYLE_TRANSFER_REFERENCE_TOOLTIP,
      referenceHandle?.id,
      referenceConnection,
      'Not connected',
    ))
  }

  const usedHandleIds = new Set([sourceHandle?.id, referenceHandle?.id].filter((id): id is string => Boolean(id)))
  for (const handle of imageHandles) {
    if (usedHandleIds.has(handle.id)) continue
    const connection = connectionFor(connections, handle.id)
    if (!connection) continue
    roles.push({
      role: 'unused',
      label: STYLE_TRANSFER_UNUSED_LABEL,
      tooltip: STYLE_TRANSFER_TEXT_ONLY_LIMIT,
      handleId: handle.id,
      url: httpUrl(connection.url),
      status: connection.failed ? 'error' : httpUrl(connection.url) ? 'connected' : connection.pending ? 'loading' : 'empty',
      message: 'This model will not use this image.',
    })
  }

  const fields: Record<string, string> = {}
  if (sourceUrl) {
    fields.image = sourceUrl
    fields[STYLE_TRANSFER_SOURCE_PARAM] = sourceUrl
  }
  if (supportsReference && referenceUrl) fields[STYLE_TRANSFER_REFERENCE_PARAM] = referenceUrl

  let blockReason: string | undefined
  if (!sourceUrl) {
    blockReason = !sourceConnection
      ? STYLE_TRANSFER_SOURCE_MISSING
      : sourceConnection.failed
        ? 'The source image couldn’t be loaded.'
        : 'The source image is still loading.'
  } else if (supportsReference && referenceConnection && !referenceUrl) {
    blockReason = referenceConnection.failed ? STYLE_TRANSFER_REFERENCE_FAILED : STYLE_TRANSFER_REFERENCE_LOADING
  }

  return {
    supportsReference,
    roles,
    fields,
    sourceUrl,
    referenceUrl: supportsReference ? referenceUrl : undefined,
    blockReason,
    precedence: supportsReference ? STYLE_TRANSFER_PRECEDENCE : undefined,
    limitation: supportsReference ? undefined : STYLE_TRANSFER_TEXT_ONLY_LIMIT,
  }
}

function isImageParam(param: NamedParam): boolean {
  return param.inputType === 'image' || param.name === STYLE_TRANSFER_SOURCE_PARAM || param.name === STYLE_TRANSFER_REFERENCE_PARAM
}

function isImageHandleId(id: string): boolean {
  return id === 'image-input' || /^i\d+$/.test(id)
}

function handleOrder(handle: StyleTransferHandleRef): number {
  if (typeof handle.ordered === 'number') return handle.ordered
  if (handle.id === 'image-input') return 0
  const numbered = /^i(\d+)$/.exec(handle.id)
  return numbered ? Number(numbered[1]) : 1000
}

function connectionFor(connections: readonly StyleTransferConnection[], handleId: string | undefined): StyleTransferConnection | undefined {
  if (!handleId) return undefined
  return connections.find((connection) => connection.handleId === handleId)
}

function httpUrl(value: string | undefined): string | undefined {
  if (!value || !/^https?:\/\//.test(value)) return undefined
  return value
}

function roleView(
  role: 'source' | 'reference',
  param: string,
  label: string,
  tooltip: string,
  handleId: string | undefined,
  connection: StyleTransferConnection | undefined,
  emptyMessage: string,
): StyleTransferRoleView {
  const url = httpUrl(connection?.url)
  if (connection?.failed) {
    return { role, param, label, tooltip, handleId, status: 'error', message: role === 'reference' ? STYLE_TRANSFER_REFERENCE_FAILED : 'Couldn’t load this image.' }
  }
  if (url) return { role, param, label, tooltip, handleId, url, status: 'connected' }
  if (connection?.pending || connection) {
    return {
      role,
      param,
      label,
      tooltip,
      handleId,
      status: connection?.pending || !url ? 'loading' : 'empty',
      message: role === 'reference' ? STYLE_TRANSFER_REFERENCE_LOADING : 'Loading',
    }
  }
  return { role, param, label, tooltip, handleId, status: 'empty', message: emptyMessage }
}
